/**
 * Per-program (outreach intent) breakdown:
 *  1. A program picker populated from /api/legacy-trust-analytics/programs.
 *  2. When a program is picked, demographic × engagement table populated
 *     from /api/legacy-trust-analytics/program/{id}/breakdown.
 *
 * Both endpoints honor the panel-level DrillDownContext, so picking a CBO
 * from "Compare CBOs" also scopes the program list and breakdown.
 */

import React, { useMemo, useState } from 'react';
import { Target, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDrillDown } from './DrillDownContext';
import {
  usePrograms,
  useProgramBreakdown,
  type ProgramRow,
  type ProgramBreakdownCell,
} from '@/hooks/ml';
import { CellSuppressionBanner } from '@/components/ml';

function Bar({ pct, color }: { pct: number; color: string }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="h-1.5 w-12 rounded-full bg-gray-200 overflow-hidden inline-block align-middle ml-1.5">
      <div className={`h-full ${color}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

function fmtPct(v: number | null | undefined): string {
  return v == null ? '—' : `${v.toFixed(2)}%`;
}

export const ProgramBreakdownCard: React.FC = () => {
  const { drillCboId } = useDrillDown();
  const programs = usePrograms(drillCboId);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const breakdown = useProgramBreakdown(selectedId, drillCboId);

  const list = useMemo<ProgramRow[]>(() => programs.data?.programs ?? [], [programs.data]);
  const selected = useMemo(
    () => list.find((p) => p.legacy_program_id === selectedId) ?? null,
    [list, selectedId],
  );
  const cells = (breakdown.data?.cells ?? []) as ProgramBreakdownCell[];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-1.5">
          <Target className="h-4 w-4 text-indigo-600" />
          Outreach intent (by program)
        </CardTitle>
        <div className="relative">
          <select
            value={selectedId ?? ''}
            onChange={(e) =>
              setSelectedId(e.target.value ? parseInt(e.target.value, 10) : null)
            }
            className="appearance-none rounded-md border border-gray-200 bg-white pr-7 pl-2 py-1 text-xs min-w-[20rem]"
            disabled={programs.isLoading || list.length === 0}
          >
            <option value="">— Pick a program —</option>
            {list.map((p) => (
              <option key={p.legacy_program_id} value={p.legacy_program_id}>
                {p.program_name || `#${p.legacy_program_id}`} ({p.total_engagements.toLocaleString()} sends,{' '}
                {p.engagement_rate_pct?.toFixed(1) ?? '—'}% engaged)
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-2 h-3 w-3 text-gray-500 pointer-events-none" />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <CellSuppressionBanner threshold={breakdown.data?.suppression_threshold ?? 100} />

        {programs.isLoading ? (
          <div className="text-sm text-gray-500">Loading programs…</div>
        ) : list.length === 0 ? (
          <div className="text-sm text-gray-500">
            No programs with outreach activity for the current scope.
          </div>
        ) : !selected ? (
          <div className="text-sm text-gray-500">
            Pick a program to see how outreach broke down across populations and languages.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Sends" value={selected.total_engagements.toLocaleString()} />
              <Stat label="Reply rate" value={fmtPct(selected.reply_rate_pct)} />
              <Stat label="Click rate" value={fmtPct(selected.click_rate_pct)} />
              <Stat label="Engagement" value={fmtPct(selected.engagement_rate_pct)} hint="reply or click" />
            </div>
            {breakdown.isLoading ? (
              <div className="text-sm text-gray-500">Loading breakdown…</div>
            ) : cells.length === 0 ? (
              <div className="text-sm text-gray-500">
                No demographic data for this program.
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[28rem]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="py-2 pr-3">Language</th>
                      <th className="py-2 pr-3">Age</th>
                      <th className="py-2 pr-3">Race / Ethnicity</th>
                      <th className="py-2 pr-3 text-right">Sends</th>
                      <th className="py-2 pr-3 text-right">Replies</th>
                      <th className="py-2 pr-3 text-right">Clicks</th>
                      <th className="py-2 pr-3 text-right">Engagement %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cells.map((c, i) => (
                      <tr key={i} className="border-b border-gray-100 last:border-0">
                        <td className="py-1.5 pr-3 font-medium text-gray-900">{c.member_language}</td>
                        <td className="py-1.5 pr-3 text-gray-600">{c.age_cohort}</td>
                        <td className="py-1.5 pr-3 text-gray-600">
                          {c.race === 'Unknown' && c.ethnicity === 'Unknown' ? '—' : `${c.race} / ${c.ethnicity}`}
                        </td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{c.engagements.toLocaleString()}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{c.replies.toLocaleString()}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{c.clicks.toLocaleString()}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">
                          {fmtPct(c.engagement_rate_pct)}
                          {c.engagement_rate_pct != null && c.engagement_rate_pct > 0 && (
                            <Bar pct={c.engagement_rate_pct} color="bg-indigo-500" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div className="rounded-md border border-gray-200 bg-white p-3">
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">{value}</div>
    {hint && <div className="text-xs text-gray-500">{hint}</div>}
  </div>
);
